import { createContext, useContext, ReactNode, Context } from 'react';
import { collection, query, where, FirestoreError, QueryConstraint } from 'firebase/firestore';
import { db, useAuthState } from 'firebaseStore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { Base, ContextProviderProps } from 'models';

export type CollectionState<T = Base> = [T[], boolean, FirestoreError | undefined, Map<string, T>];

const states: { [key: string]: Context<CollectionState | undefined> | undefined } = {};
export const getCollectionContext = (collectionName: string) => {
    if (!states[collectionName]) {
        states[collectionName] = createContext<CollectionState | undefined>(undefined);
    }
    return states[collectionName] as Context<CollectionState | undefined>;
};

const getKey = (collectionName: string, constraints?: QueryConstraint[]) => {
    if (!constraints) return collectionName;
    if (!constraints.length) return collectionName;
    const constraintsString = constraints
        .map((constraint) => {
            if (constraint.type === 'where') {
                // @ts-ignore
                return `${constraint.type}-${constraint.za}-${JSON.stringify(constraint.Ha)}`;
            }
            if (constraint.type === 'orderBy') {
                // @ts-ignore
                return `${constraint.type}-${constraint.Ja}-${JSON.stringify(
                    // @ts-ignore
                    constraint.Ga.segments
                )}`;
            }
            // console.info('constraint', constraint);
            // console.info('constraint type', constraint.type);
            // // @ts-ignore
            // console.info('constraint za', constraint?.za);
            // // @ts-ignore
            // console.info('constraint Ha', constraint?.Ha);
            throw new Error(`only where, orderBy constraints allowed, not ${constraint.type}`);
        })
        .join('***');
    return `${collectionName}***${constraintsString}`;
};

export interface CollectionProviderProps<T> extends ContextProviderProps<T> {
    children: ReactNode | undefined;
}
export const CollectionProvider = <T extends Base>({
    children,
    collectionName,
    converter,
    constraints,
    keyFunction,
}: CollectionProviderProps<T>) => {
    const key = getKey(collectionName, constraints);
    const ActualContext = getCollectionContext(key);
    const [, , tenant] = useAuthState();
    const collectionRef = collection(db, collectionName).withConverter(converter);
    const finalConstraints = constraints?.length ? constraints : [where('tenantId', '==', tenant)];
    const recordQuery = query(collectionRef, ...finalConstraints);
    const [records, pending, error] = useCollectionData<T>(recordQuery, { idField: 'id' });
    const recordMap = new Map(
        records
            ? records.map((record) => [
                  keyFunction ? keyFunction(record) : record.indexId(),
                  record,
              ])
            : []
    );
    return (
        <ActualContext.Provider value={[records || [], pending, error, recordMap]}>
            {children}
        </ActualContext.Provider>
    );
};

export function useCollectionContext(collectionName: string, constraints?: QueryConstraint[]) {
    const key = getKey(collectionName, constraints);
    const context = useContext(getCollectionContext(key));
    if (context === undefined) {
        throw new Error(
            `useCollectionContext must be used within a matching CollectionProvider ${collectionName} : ${
                constraints?.length || 0
            }`
        );
    }
    return context;
}

export function useOptionalCollectionContext(
    collectionName: string,
    constraints?: QueryConstraint[]
) {
    const key = getKey(collectionName, constraints);
    const context = useContext(getCollectionContext(key));
    return context || [undefined, false, undefined, new Map<string, Base>()];
}
